// src/components/AppDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpDown,
  Calendar,
  Filter,
  RefreshCw,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchRfqList, type RfqListItemDto } from '../lib/rfqApi';
import { fetchQuotationList } from '../lib/quotationApi';
import { ModuleInfoButton } from './ModuleInfoButton';
import LoadingState from './LoadingState';
import Button from './ui/Button';
import SearchInput from './ui/SearchInput';
import { ErrorState } from './ui/States';
import RfqLifecycleList from '../features/rfqLifecycle/RfqLifecycleList';
import { useRfqLifecycles } from '../features/rfqLifecycle/useRfqLifecycles';
import { searchText as lifecycleSearchText } from '../features/rfqLifecycle/lifecycleModel';
import { formatDate as fmtDate } from "../utils/dateFormat";

const DASHBOARD_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>The Insights dashboard shows RFQs moving through your workflow stages.</p>
    <p className="font-medium">You can:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>
        See every RFQ with its quotation, order, vendor POs, invoices, credit notes and payment on one
        line, and click a box to open that document
      </li>
      <li>Filter by date range and search RFQs</li>
      <li>Sort by date, client, cost or item count</li>
    </ul>
  </div>
);

type SortMode = 'date' | 'name' | 'items' | 'cost';
type FilterTab = 'date' | 'items' | 'cost' | 'clients';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const formatDateShort = (value: Date) => fmtDate(value);

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, delta: number) => new Date(d.getFullYear(), d.getMonth() + delta, 1);
const sameDay = (a?: Date | null, b?: Date | null) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const toDateOnly = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const buildMonthGrid = (monthStart: Date) => {
  const firstDay = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
  const startOffset = firstDay.getDay();
  const gridStart = new Date(firstDay);
  gridStart.setDate(firstDay.getDate() - startOffset);

  const days: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + i);
    days.push(day);
  }

  return days;
};

/**
 * The keys one RFQ's quotation cost can be filed under in `quotationCostMap`.
 *
 * Two, because `loadBoard` fills the map from the quotation list and a quotation
 * carries the RFQ's mongo id on some rows and its human number on others. Both
 * the filter and the sort resolve it the same way now, which is the point of the
 * function existing at all — they used to resolve it differently and only one of
 * them worked.
 */
const rfqCostKeys = (rfq: RfqListItemDto): string[] => [
  String((rfq as any)._id || ''),
  String((rfq as any).rfqId || '').toUpperCase(),
];

/**
 * An RFQ's order cost, from the quotation costs this component already loaded.
 *
 * This used to read `rfq.orderCost ?? totalCost ?? totalPrice ?? total ??
 * totalAmount` off the RFQ itself, behind an `as any`. Not one of those fields
 * exists on `RfqListItemDto` — the `as any` is what let it compile — so it
 * returned null for every row. The consequences were silent rather than broken:
 * the cost SORT compared -1 against -1 for every pair and left the list exactly
 * as it found it, and the modal's cost read blank. Meanwhile the cost FILTER a
 * hundred lines below did it correctly off `quotationCostMap`, so filtering by
 * cost worked and sorting by cost did not, in one component, off one dataset.
 * (TECH_DEBT #45)
 */
const getOrderCost = (
  rfq: RfqListItemDto,
  costMap: Record<string, number>
): number | null => {
  for (const key of rfqCostKeys(rfq)) {
    if (key && costMap[key] != null) return costMap[key];
  }
  return null;
};

const cleanName = (name?: string) => {
  if (!name) return 'Unknown customer';
  const withoutEmail = name.split('<')[0];
  const cleaned = withoutEmail.replace(/["'<>,]/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned || 'Unknown customer';
};

/** What the board's search box looks at on an RFQ. One owner, so the Board and Lifecycle views match alike. */
const rfqSearchText = (rfq: RfqListItemDto): string =>
  [rfq.rfqId, rfq.customerName, rfq.subject, rfq.vesselName].filter(Boolean).join(' ').toLowerCase();

/** The board's sort, shared by both views. Returns a new array. */
const sortRfqList = (
  rfqs: RfqListItemDto[],
  sortMode: SortMode,
  quotationCostMap: Record<string, number>
): RfqListItemDto[] => {
  const list = [...rfqs];

  if (sortMode === 'name') {
    list.sort((a, b) => cleanName(a.customerName).localeCompare(cleanName(b.customerName)));
  } else if (sortMode === 'cost') {
    list.sort((a, b) => {
      const ca = getOrderCost(a, quotationCostMap) ?? -1;
      const cb = getOrderCost(b, quotationCostMap) ?? -1;
      return cb - ca;
    });
  } else if (sortMode === 'items') {
    list.sort((a, b) => (b.itemCount || 0) - (a.itemCount || 0));
  } else {
    list.sort((a, b) => {
      const da = a.receivedAt ? new Date(a.receivedAt).getTime() : 0;
      const db = b.receivedAt ? new Date(b.receivedAt).getTime() : 0;
      return db - da;
    });
  }

  return list;
};

const AppDashboard: React.FC = () => {
  const { user } = useAuth();

  // Compact layout: on narrow (<=1024px) OR short (<=800px) viewports, let the page
  // scroll vertically so the Insights/KPI header can scroll away and the board gets
  // real height. On large + tall screens this stays false and the layout is unchanged.
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px), (max-height: 800px)');
    const update = () => setCompact(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const [rfqs, setRfqs] = useState<RfqListItemDto[]>([]);
  // Counts finished loads of the board, so the Lifecycle view rereads on a Refresh that returns the same RFQs.
  const [boardLoads, setBoardLoads] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);


  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('date');
  const [sortOpen, setSortOpen] = useState(false);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('date');
  const [filterCalendarMonth, setFilterCalendarMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [calendarLeftMonth, setCalendarLeftMonth] = useState<Date>(() => startOfMonth(new Date()));

  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });

  const [statusFilter, setStatusFilter] = useState<Record<string, boolean>>({
    processing: true,
    processed: true,
    ready: true,
  });

  const [itemRange, setItemRange] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [costRange, setCostRange] = useState<{ min: string; max: string }>({ min: '', max: '' });
  // rfqId (string) → quotation totalValue — populated from the quotation list on load
  const [quotationCostMap, setQuotationCostMap] = useState<Record<string, number>>({});
  const [clientFilterMap, setClientFilterMap] = useState<Record<string, boolean>>({});

  const loadBoard = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rfqRes, quoteRes] = await Promise.all([
        fetchRfqList('all', 1, 200),
        fetchQuotationList({ page: 1, limit: 500 }).catch(() => null),
      ]);
      setRfqs(rfqRes.data || []);
      setBoardLoads((n) => n + 1);

      // Build rfqId → quotation totalValue map for cost filter
      const costMap: Record<string, number> = {};
      (quoteRes?.data?.quotations || []).forEach((q: any) => {
        const val = q.summary?.totalValue;
        if (typeof val !== 'number') return;
        const rfqId = typeof q.rfqId === 'object' ? q.rfqId?._id : q.rfqId;
        if (rfqId) costMap[String(rfqId)] = val;
        if (q.rfqNumber) costMap[String(q.rfqNumber).toUpperCase()] = val;
      });
      setQuotationCostMap(costMap);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to load board data:', err);
      setError(err.message || 'Failed to load board data');
      setRfqs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!datePickerOpen) return;
    const base = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : new Date();
    setCalendarLeftMonth(startOfMonth(base));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePickerOpen]);

  useEffect(() => {
    if (!filterOpen) return;
    const base = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : new Date();
    setFilterCalendarMonth(startOfMonth(base));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOpen]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await loadBoard();
    setRefreshing(false);
  };

  const handleCalendarPick = (picked: Date) => {
    const pickedIso = picked.toISOString().slice(0, 10);

    // First click OR range already complete → start a fresh selection (no end yet)
    if (!dateRange.start || dateRange.end) {
      setDateRange({ start: pickedIso, end: '' });
      return;
    }

    // Second click → complete the range, swap if needed
    const start = new Date(`${dateRange.start}T00:00:00`);
    if (picked.getTime() < start.getTime()) {
      setDateRange({ start: pickedIso, end: dateRange.start });
    } else {
      setDateRange({ start: dateRange.start, end: pickedIso });
    }
  };

  const filteredRfqs = useMemo(() => {
    const activeStatuses = Object.keys(statusFilter).filter((k) => statusFilter[k]);
    const startDate = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
    const endDate = dateRange.end ? new Date(`${dateRange.end}T23:59:59`) : null;
    const minItems = itemRange.min ? Number(itemRange.min) : null;
    const maxItems = itemRange.max ? Number(itemRange.max) : null;
    const minCost = costRange.min ? Number(costRange.min) : null;
    const maxCost = costRange.max ? Number(costRange.max) : null;
    const activeClients = Object.keys(clientFilterMap).filter((k) => clientFilterMap[k]);
    return (rfqs || []).filter((r) => {
      const s = String(r.status || '').toLowerCase();
      // Status is an explicit checkbox set: unchecking all hides everything
      // (empty selection = nothing matches, not "no filter").
      if (!activeStatuses.includes(s)) return false;

      if (startDate || endDate) {
        const raw = r.receivedAt;
        if (!raw) return true;
        const t = new Date(raw).getTime();
        if (Number.isNaN(t)) return true;
        if (startDate && t < startDate.getTime()) return false;
        if (endDate && t > endDate.getTime()) return false;
      }

      if (activeClients.length) {
        const name = cleanName(r.customerName);
        if (!activeClients.includes(name)) return false;
      }

      if (minItems !== null || maxItems !== null) {
        const count = Number(r.itemCount || 0);
        if (minItems !== null && count < minItems) return false;
        if (maxItems !== null && count > maxItems) return false;
      }

      if (minCost !== null || maxCost !== null) {
        // Same lookup the sort uses — see getOrderCost. This block was the
        // correct one; the sort was reading fields that do not exist.
        const cost = getOrderCost(r, quotationCostMap);
        if (cost !== null) {
          if (minCost !== null && cost < minCost) return false;
          if (maxCost !== null && cost > maxCost) return false;
        }
      }

      return true;
    });
  }, [rfqs, statusFilter, dateRange, itemRange, costRange, clientFilterMap, quotationCostMap]);

  const clientOptions = useMemo(() => {
    const map = new Map<string, number>();
    (rfqs || []).forEach((r) => {
      const name = cleanName(r.customerName);
      map.set(name, (map.get(name) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rfqs]);

  // Status filter options derived from the actual RFQ data (with counts), so the
  // checkboxes always match what's really in the board — including statuses like
  // "error" that the old hardcoded list omitted, and excluding ones with no RFQs.
  const statusOptions = useMemo(() => {
    const map = new Map<string, number>();
    (rfqs || []).forEach((r) => {
      const s = String(r.status || '').toLowerCase().trim() || '(unknown)';
      map.set(s, (map.get(s) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);
  }, [rfqs]);

  const maxItemCount = useMemo(() => {
    return (rfqs || []).reduce((max, r) => Math.max(max, Number(r.itemCount || 0)), 0);
  }, [rfqs]);

  const maxOrderCost = useMemo(() => {
    // Use quotation totalValue (keyed by rfq._id or rfqId) as the cost source
    const values = Object.values(quotationCostMap);
    if (values.length === 0) return 0;
    return Math.max(0, ...values);
  }, [quotationCostMap]);

  const allClientsSelected = useMemo(() => {
    if (!clientOptions.length) return false;
    return clientOptions.every((c) => clientFilterMap[c.name]);
  }, [clientOptions, clientFilterMap]);

  const searchFilteredRfqs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return filteredRfqs;

    return filteredRfqs.filter((rfq) => rfqSearchText(rfq).includes(term));
  }, [filteredRfqs, searchTerm]);

  const sortedRfqs = useMemo(
    () => sortRfqList(searchFilteredRfqs, sortMode, quotationCostMap),
    // quotationCostMap is a dependency now: the cost sort reads it, and it is
    // filled asynchronously by loadBoard. Without it the memo would settle
    // before the costs arrive and never re-sort — which would look exactly like
    // the bug this replaced.
    [searchFilteredRfqs, sortMode, quotationCostMap]
  );

  const lifecycles = useRfqLifecycles(rfqs, true, boardLoads);

  // The search box also finds an RFQ by anything on its trail: a quotation, PO or invoice number, or a vendor.
  const lifecycleRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sortedRfqs;
    const matched = filteredRfqs.filter(
      (rfq) => rfqSearchText(rfq).includes(term) || lifecycleSearchText(lifecycles.forRfq(rfq._id)).includes(term)
    );
    return sortRfqList(matched, sortMode, quotationCostMap);
  }, [searchTerm, sortedRfqs, filteredRfqs, lifecycles, sortMode, quotationCostMap]);

  if (!user) {
    return (
      <div className="h-full bg-brand-bg">
        <LoadingState text="Loading dashboard..." />
      </div>
    );
  }

  const rangeStart = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
  const rangeEnd = dateRange.end ? new Date(`${dateRange.end}T00:00:00`) : null;

  return (
    <div className={`h-full flex flex-col bg-brand-bg ${compact ? 'overflow-y-auto' : ''}`}>
      {/* The app's header band. The title is the sidebar's word for this
          screen — the nav says "Home" and the page used to answer "Insights". */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1 shrink-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold leading-7 text-slate-900">Home</h1>
              <ModuleInfoButton content={DASHBOARD_INFO_CONTENT} ariaLabel="Insights dashboard information" />
            </div>
            <p className="text-xs text-slate-500">Track the flow of RFQs across stages</p>
          </div>

          <div className="relative">
            <Button
              onClick={() => setDatePickerOpen((prev) => !prev)}
              icon={<Calendar aria-hidden="true" className="h-4 w-4" />}
            >
              {rangeStart ? formatDateShort(rangeStart) : 'Start'} - {rangeEnd ? formatDateShort(rangeEnd) : 'End'}
            </Button>

            {datePickerOpen && (
              <div className="absolute right-0 mt-2 w-[660px] rounded-xl border border-slate-200 bg-white shadow-xl p-5 z-30">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm font-semibold text-slate-800">Date range</div>
                  <div className="text-xs text-slate-500">
                    {rangeStart && rangeEnd
                      ? `${formatDateShort(rangeStart)} - ${formatDateShort(rangeEnd)}`
                      : rangeStart
                      ? `From ${formatDateShort(rangeStart)}`
                      : 'Select start date'}
                  </div>
                </div>

                {/* Month navigation */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => setCalendarLeftMonth((prev) => addMonths(prev, -1))}
                    className="h-7 w-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center text-base"
                  >‹</button>

                  <div className="flex items-center gap-8">
                    {[0, 1].map((offset) => {
                      const m = addMonths(calendarLeftMonth, offset);
                      return (
                        <div key={offset} className="flex items-center gap-1">
                          <select
                            value={m.getMonth()}
                            onChange={(e) => setCalendarLeftMonth(new Date(m.getFullYear(), Number(e.target.value) - offset, 1))}
                            className="text-xs font-semibold text-slate-700 border-none bg-transparent focus:outline-none cursor-pointer"
                          >
                            {MONTHS.map((mn, i) => <option key={i} value={i}>{mn}</option>)}
                          </select>
                          <select
                            value={m.getFullYear()}
                            onChange={(e) => setCalendarLeftMonth(new Date(Number(e.target.value), m.getMonth() - offset, 1))}
                            className="text-xs font-semibold text-slate-700 border-none bg-transparent focus:outline-none cursor-pointer"
                          >
                            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 4 + i).map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCalendarLeftMonth((prev) => addMonths(prev, 1))}
                    className="h-7 w-7 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center text-base"
                  >›</button>
                </div>

                {/* Two-month grid */}
                <div className="grid grid-cols-2 gap-6">
                  {[0, 1].map((offset) => {
                    const monthStart = addMonths(calendarLeftMonth, offset);
                    const monthDays = buildMonthGrid(monthStart);
                    const start = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
                    const end = dateRange.end ? new Date(`${dateRange.end}T00:00:00`) : null;
                    const hasFullRange = !!start && !!end && dateRange.start !== dateRange.end;

                    return (
                      <div key={offset}>
                        <div className="grid grid-cols-7 text-xs text-slate-400 mb-1 font-medium">
                          {WEEKDAYS.map((d) => <div key={d} className="text-center py-1">{d}</div>)}
                        </div>
                        {/* No gap — cells touch so strip is continuous */}
                        <div className="grid grid-cols-7">
                          {monthDays.map((day) => {
                            const inMonth = day.getMonth() === monthStart.getMonth();
                            const isStart = sameDay(day, start);
                            const isEnd = sameDay(day, end);
                            const inRange = !!(start && end &&
                              toDateOnly(day).getTime() >= toDateOnly(start).getTime() &&
                              toDateOnly(day).getTime() <= toDateOnly(end).getTime());

                            return (
                              <div key={day.toISOString()} className="relative h-9 flex items-center justify-center">
                                {/* Connected strip background */}
                                {inRange && hasFullRange && !isStart && !isEnd && inMonth && (
                                  <div className="absolute inset-0 bg-cyan-100" />
                                )}
                                {isStart && hasFullRange && inMonth && (
                                  <div className="absolute top-0 bottom-0 left-1/2 right-0 bg-cyan-100" />
                                )}
                                {isEnd && hasFullRange && inMonth && (
                                  <div className="absolute top-0 bottom-0 left-0 right-1/2 bg-cyan-100" />
                                )}

                                {/* Date circle */}
                                <button
                                  onClick={inMonth ? () => handleCalendarPick(day) : undefined}
                                  className={[
                                    'relative z-10 h-8 w-8 rounded-full flex items-center justify-center text-xs transition-colors',
                                    !inMonth
                                      ? 'text-slate-300 cursor-default'
                                      : isStart || isEnd
                                      ? 'bg-primary-500 text-white font-semibold'
                                      : inRange
                                      ? 'text-slate-800'
                                      : 'text-slate-700 hover:bg-cyan-50 cursor-pointer',
                                  ].join(' ')}
                                >
                                  {day.getDate()}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="mt-5 flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => { setDateRange({ start: '', end: '' }); }}>
                    Clear
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setDatePickerOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => setDatePickerOpen(false)}>
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      <div className={compact ? 'flex flex-col px-8 pb-6 pt-4' : 'flex-1 flex flex-col min-h-0 px-8 pb-6 pt-4 overflow-hidden'}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2" />

          <div className="flex flex-wrap items-center gap-2">
            <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search here" className="w-56" />

            <div className="relative">
              <Button
                size="sm"
                onClick={() => setSortOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={sortOpen}
                icon={<ArrowUpDown aria-hidden="true" className="h-3.5 w-3.5" />}
              >
                Sort by
              </Button>
              {sortOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-md border border-slate-200 bg-white shadow-lg p-2 z-20">
                  {[
                    { id: 'name', label: 'By A-Z' },
                    { id: 'date', label: 'By date' },
                    { id: 'cost', label: 'By cost' },
                    { id: 'items', label: 'By number of items' },
                  ].map((opt) => (
                    <label
                      key={opt.id}
                      className="flex items-center gap-2 text-xs text-slate-700 py-1 px-1 rounded hover:bg-slate-50"
                    >
                      <input
                        type="radio"
                        name="sortMode"
                        value={opt.id}
                        checked={sortMode === opt.id}
                        onChange={() => {
                          setSortMode(opt.id as SortMode);
                          setSortOpen(false);
                        }}
                        className="h-3 w-3"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <Button size="sm" onClick={() => setFilterOpen(true)} icon={<Filter aria-hidden="true" className="h-3.5 w-3.5" />}>
              Filter
            </Button>

            <Button
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              loading={refreshing}
              icon={<RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />}
            >
              Refresh
            </Button>
          </div>
        </div>


        {loading ? (
          <div className="h-[60vh] bg-white border border-slate-200 rounded-md shadow-sm">
            <LoadingState text="Loading board..." />
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={loadBoard} />
        ) : (
          <div className={compact ? '' : 'flex-1 min-h-0 overflow-y-auto pr-1'}>
            <RfqLifecycleList rfqs={lifecycleRows} lifecycles={lifecycles} clientName={(rfq) => cleanName(rfq.customerName)} />
          </div>
        )}

        {filterOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/20" onClick={() => setFilterOpen(false)} />
            <div className="relative w-full max-w-3xl bg-white rounded-lg border border-slate-200 shadow-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">Filter</div>
                <button
                  onClick={() => setFilterOpen(false)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-slate-100 text-slate-600"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex min-h-[320px]">
                <div className="w-44 border-r border-slate-200 bg-slate-50 p-3 space-y-1 text-xs">
                  {[
                    { id: 'date', label: 'Date' },
                    { id: 'items', label: 'Item range' },
                    { id: 'cost', label: 'Order Cost' },
                    { id: 'clients', label: 'Client Name' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setFilterTab(tab.id as FilterTab)}
                      className={[
                        'w-full text-left px-2 py-1 rounded-md',
                        filterTab === tab.id ? 'bg-white border border-slate-200 text-slate-800' : 'text-slate-600',
                      ].join(' ')}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="flex-1 p-4">
                  {filterTab === 'date' && (
                    <div className="space-y-4">
                      <div>
                        <div className="text-xs font-semibold text-slate-700 mb-2">Status</div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          {statusOptions.length === 0 ? (
                            <div className="text-xs text-slate-400">No statuses found.</div>
                          ) : (
                            statusOptions.map(({ status, count }) => (
                              <label key={status} className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={!!statusFilter[status]}
                                  onChange={(e) =>
                                    setStatusFilter((prev) => ({ ...prev, [status]: e.target.checked }))
                                  }
                                  className="h-3 w-3"
                                />
                                <span className="capitalize flex-1 truncate">{status}</span>
                                <span className="text-xs text-slate-400">{count}</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-3">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-xs font-semibold text-slate-700">Date range</div>
                          <div className="text-xs text-slate-500">
                            {rangeStart && rangeEnd
                              ? `${formatDateShort(rangeStart)} - ${formatDateShort(rangeEnd)}`
                              : rangeStart ? `From ${formatDateShort(rangeStart)}` : 'Select dates'}
                          </div>
                        </div>

                        {/* Month navigation */}
                        <div className="flex items-center justify-between mb-3">
                          <button onClick={() => setFilterCalendarMonth((p) => addMonths(p, -1))}
                            className="h-6 w-6 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center">‹</button>
                          <div className="flex items-center gap-6">
                            {[0, 1].map((offset) => {
                              const m = addMonths(filterCalendarMonth, offset);
                              return (
                                <div key={offset} className="flex items-center gap-1">
                                  <select value={m.getMonth()}
                                    onChange={(e) => setFilterCalendarMonth(new Date(m.getFullYear(), Number(e.target.value) - offset, 1))}
                                    className="text-xs font-semibold text-slate-700 border-none bg-transparent focus:outline-none cursor-pointer">
                                    {MONTHS.map((mn, i) => <option key={i} value={i}>{mn}</option>)}
                                  </select>
                                  <select value={m.getFullYear()}
                                    onChange={(e) => setFilterCalendarMonth(new Date(Number(e.target.value), m.getMonth() - offset, 1))}
                                    className="text-xs font-semibold text-slate-700 border-none bg-transparent focus:outline-none cursor-pointer">
                                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 4 + i).map((y) => (
                                      <option key={y} value={y}>{y}</option>
                                    ))}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                          <button onClick={() => setFilterCalendarMonth((p) => addMonths(p, 1))}
                            className="h-6 w-6 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center">›</button>
                        </div>

                        {/* Two-month grid */}
                        <div className="grid grid-cols-2 gap-4">
                          {[0, 1].map((offset) => {
                            const monthStart = addMonths(filterCalendarMonth, offset);
                            const monthDays = buildMonthGrid(monthStart);
                            const start = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
                            const end = dateRange.end ? new Date(`${dateRange.end}T00:00:00`) : null;
                            const hasFullRange = !!start && !!end && dateRange.start !== dateRange.end;

                            return (
                              <div key={offset}>
                                <div className="grid grid-cols-7 text-xs text-slate-400 font-medium mb-1">
                                  {WEEKDAYS.map((d) => <div key={d} className="text-center py-0.5">{d}</div>)}
                                </div>
                                <div className="grid grid-cols-7">
                                  {monthDays.map((day) => {
                                    const inMonth = day.getMonth() === monthStart.getMonth();
                                    const isStart = sameDay(day, start);
                                    const isEnd = sameDay(day, end);
                                    const inRange = !!(start && end &&
                                      toDateOnly(day).getTime() >= toDateOnly(start).getTime() &&
                                      toDateOnly(day).getTime() <= toDateOnly(end).getTime());

                                    return (
                                      <div key={day.toISOString()} className="relative h-8 flex items-center justify-center">
                                        {inRange && hasFullRange && !isStart && !isEnd && inMonth && (
                                          <div className="absolute inset-0 bg-cyan-100" />
                                        )}
                                        {isStart && hasFullRange && inMonth && (
                                          <div className="absolute top-0 bottom-0 left-1/2 right-0 bg-cyan-100" />
                                        )}
                                        {isEnd && hasFullRange && inMonth && (
                                          <div className="absolute top-0 bottom-0 left-0 right-1/2 bg-cyan-100" />
                                        )}
                                        <button
                                          onClick={inMonth ? () => handleCalendarPick(day) : undefined}
                                          className={[
                                            'relative z-10 h-7 w-7 rounded-full flex items-center justify-center text-xs transition-colors',
                                            !inMonth ? 'text-slate-300 cursor-default'
                                              : isStart || isEnd ? 'bg-primary-500 text-white font-semibold'
                                              : inRange ? 'text-slate-800'
                                              : 'text-slate-700 hover:bg-cyan-50 cursor-pointer',
                                          ].join(' ')}
                                        >
                                          {day.getDate()}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="mt-3 flex items-center justify-between">
                          <button onClick={() => setDateRange({ start: '', end: '' })}
                            className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
                          <div className="flex items-center gap-3">
                            <button onClick={() => setFilterOpen(false)}
                              className="text-xs font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
                            <button onClick={() => setFilterOpen(false)}
                              className="px-3 py-1 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600">OK</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {filterTab === 'items' && (
                    <div className="space-y-4">
                      <div className="text-xs font-semibold text-slate-700">Number of Items</div>
                      <div className="rounded-md border border-slate-200 px-3 py-3">
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span>0</span>
                          <span>{itemRange.max || Math.max(maxItemCount, 0)}</span>
                          <span>{Math.max(maxItemCount, 0)}</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={Math.max(maxItemCount, 0)}
                          value={itemRange.max || Math.max(maxItemCount, 0)}
                          onChange={(e) =>
                            setItemRange({ min: '0', max: e.target.value })
                          }
                          className="w-full accent-cyan-600"
                        />
                      </div>
                    </div>
                  )}

                  {filterTab === 'cost' && (
                    <div className="space-y-4">
                      <div className="text-xs font-semibold text-slate-700">Total Order Cost (from Quotations)</div>
                      {maxOrderCost === 0 ? (
                        <div className="rounded-md border border-slate-200 px-4 py-6 text-center">
                          <div className="text-xs text-slate-400">No quotation cost data available yet.</div>
                          <div className="text-xs text-slate-300 mt-1">Generate quotations to enable cost filtering.</div>
                        </div>
                      ) : (
                        <>
                          <div className="rounded-md border border-slate-200 px-3 py-3">
                            <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                              <span>$0</span>
                              {/* Editable max value — click to type a precise number */}
                              <div className="flex items-center gap-1 text-cyan-600 font-semibold">
                                <span>Up to $</span>
                                <input
                                  type="number"
                                  min={0}
                                  max={Math.round(maxOrderCost)}
                                  value={costRange.max}
                                  placeholder={String(Math.round(maxOrderCost))}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    const n = Number(v);
                                    if (!v) { setCostRange({ min: '', max: '' }); return; }
                                    const clamped = Math.min(Math.max(0, n), Math.round(maxOrderCost));
                                    setCostRange({ min: '0', max: String(clamped) });
                                  }}
                                  className="w-24 text-center text-cyan-600 font-semibold text-xs border border-cyan-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-cyan-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                />
                              </div>
                              <span>${Math.round(maxOrderCost).toLocaleString()}</span>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={Math.round(maxOrderCost)}
                              value={costRange.max || Math.round(maxOrderCost)}
                              onChange={(e) => setCostRange({ min: '0', max: e.target.value })}
                              className="w-full accent-cyan-600"
                            />
                          </div>
                          <div className="text-xs text-slate-400">
                            Shows RFQs whose linked quotation total is within the selected range.
                            {costRange.max && Number(costRange.max) < Math.round(maxOrderCost) && (
                              <button
                                onClick={() => setCostRange({ min: '', max: '' })}
                                className="ml-2 text-rose-400 hover:underline"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {filterTab === 'clients' && (
                    <div className="space-y-3">
                      <div className="text-xs font-semibold text-slate-700">Select Client Names</div>
                      <div className="max-h-48 overflow-y-auto pr-2 space-y-1 text-xs text-slate-600">
                        {clientOptions.length === 0 ? (
                          <div className="text-xs text-slate-400">No clients found.</div>
                        ) : (
                          <>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={allClientsSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const next: Record<string, boolean> = {};
                                    clientOptions.forEach((c) => { next[c.name] = true; });
                                    setClientFilterMap(next);
                                  } else {
                                    setClientFilterMap({});
                                  }
                                }}
                                className="h-3 w-3"
                              />
                              <span className="flex-1 truncate">Select All</span>
                            </label>
                            {clientOptions.map((client) => (
                              <label key={client.name} className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={!!clientFilterMap[client.name]}
                                  onChange={(e) =>
                                    setClientFilterMap((prev) => ({
                                      ...prev,
                                      [client.name]: e.target.checked,
                                    }))
                                  }
                                  className="h-3 w-3"
                                />
                                <span className="flex-1 truncate">{client.name}</span>
                                <span className="text-xs text-slate-400">{client.count}</span>
                              </label>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => {
                    setStatusFilter({ processing: true, processed: true, ready: true });
                    setDateRange({ start: '', end: '' });
                    setItemRange({ min: '', max: '' });
                    setCostRange({ min: '', max: '' });
                    setClientFilterMap({});
                  }}
                  className="px-4 py-1.5 rounded-md border border-slate-200 text-xs text-slate-600"
                >
                  Clear
                </button>
                <button
                  onClick={() => setFilterOpen(false)}
                  className="px-4 py-1.5 rounded-md bg-primary-500 text-xs font-semibold text-white"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AppDashboard;
